/**
 * Phase 1C (#169) — compliance pre-checks + disclaimer auto-injection.
 * Extracted from NewsletterAIService.js as part of #229 decomposition.
 *
 * Public exports:
 *   - runCompliancePrechecks({ content, seriesMeta }) — scans HTML/plain-text
 *     for SEC marketing rule violations. Runs regardless of feature flag
 *     (defense-in-depth). Returns { warnings, fatal }.
 *   - applyComplianceDisclaimer({ html, series, userBranding }) — appends
 *     the resolved disclaimer to outgoing HTML. Resolution order:
 *     series.complianceDisclaimerOverride > userBranding.complianceDisclaimer
 *     > niche default. Returns HTML unchanged when nothing resolves.
 *   - NICHE_DEFAULT_DISCLAIMERS — exported for testability.
 */

const SEC_HIGH_SEVERITY_RETIREES_TERMS = ['guaranteed', 'guarantee', 'no risk', 'risk-free'];
const GENERIC_MEDIUM_SEVERITY_TERMS = ['will outperform', 'cannot lose'];

const stripHtml = (input) => {
  if (typeof input !== 'string') return '';
  return input.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const extractContext = (text, term, radius = 60) => {
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx < 0) return '';
  const start = Math.max(0, idx - Math.floor(radius / 2));
  const end = Math.min(text.length, idx + term.length + Math.floor(radius / 2));
  return (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '');
};

/**
 * Phase 1C — compliance pre-checks for AI-generated newsletter content.
 * Runs regardless of feature flag (defense-in-depth).
 */
export async function runCompliancePrechecks({ content, seriesMeta } = {}) {
  const text = stripHtml(content);
  const lower = text.toLowerCase();
  const warnings = [];

  if (!text) return { warnings, fatal: false };

  const niche = seriesMeta?.niche;

  if (niche === 'retirees') {
    for (const term of SEC_HIGH_SEVERITY_RETIREES_TERMS) {
      if (lower.includes(term)) {
        warnings.push({
          severity: 'high',
          rule: 'sec_marketing_rule_206',
          message: `'${term}' may violate SEC Marketing Rule 206(4)-1 for retiree audiences`,
          snippet: extractContext(text, term, 60),
        });
      }
    }
  }

  for (const term of GENERIC_MEDIUM_SEVERITY_TERMS) {
    if (lower.includes(term)) {
      warnings.push({
        severity: 'medium',
        rule: 'sec_marketing_rule_general',
        message: `'${term}' is a non-specific projection that may require qualification`,
        snippet: extractContext(text, term, 60),
      });
    }
  }

  const fatal = warnings.some((w) => w.severity === 'high');
  return { warnings, fatal };
}

export const NICHE_DEFAULT_DISCLAIMERS = {
  retirees:
    'Past performance does not guarantee future results. Investment advice provided by your financial advisor; consult before making decisions about retirement income, RMDs, or estate planning.',
  hnw:
    'Past performance does not guarantee future results. Tax and estate strategies discussed are general in nature; consult your advisor for guidance specific to your situation.',
  tech_employees:
    'Past performance does not guarantee future results. RSU, ISO, and concentrated-stock strategies are general guidance; consult your advisor before acting.',
  medical_professionals:
    'Past performance does not guarantee future results. General guidance only; consult your advisor for situation-specific advice on student loans, disability insurance, and self-employment tax.',
  busy_parents:
    'Past performance does not guarantee future results. General guidance only; consult your advisor before making decisions about college savings, life insurance, or major purchases.',
};

/**
 * Phase 1C — append the resolved compliance disclaimer to the outgoing HTML.
 * Resolution: series.complianceDisclaimerOverride > userBranding.complianceDisclaimer > niche default.
 * If none found, returns the HTML unchanged.
 *
 * Disclaimer text is plain-text user input (set via series settings or branding),
 * so it MUST be HTML-escaped before interpolation to prevent XSS via the email
 * footer. (See review of PR #234 — same XSS class as Phase 1B `safeColor()` fix.)
 */
// Review consolidation: shared with the email shell's preheader/title
// escaping — one security-relevant implementation, not two.
import { escapeHtmlText } from '../../utils/escapeHtml.js';

export async function applyComplianceDisclaimer({ html, series, userBranding } = {}) {
  if (html == null) return html;

  const seriesOverride = series?.complianceDisclaimerOverride;
  const brandingDefault = userBranding?.complianceDisclaimer;
  const nicheFallback = series?.niche ? NICHE_DEFAULT_DISCLAIMERS[series.niche] : null;

  const disclaimer =
    (typeof seriesOverride === 'string' && seriesOverride.trim()) ||
    (typeof brandingDefault === 'string' && brandingDefault.trim()) ||
    nicheFallback ||
    null;

  if (!disclaimer) return html;

  // #392 — idempotency. Stored campaign content is the disclaimer-injected
  // HTML (#361, so the scheduled executor ships it verbatim). Duplicating a
  // sent campaign clones that content and re-guards it on the next send —
  // without an idempotency check each duplicate→resend stacked another
  // disclaimer paragraph.
  //
  // We skip re-appending when EITHER signal says the disclaimer is already
  // present:
  //   (a) the marker attribute on our own <p> (fast path, exact), OR
  //   (b) the escaped disclaimer TEXT already appears in the HTML — this
  //       survives a WYSIWYG round-trip that strips unknown data-* attributes
  //       (which would otherwise re-stack a second disclaimer).
  // The marker uses the full opening-tag prefix so a stray pasted attribute
  // in unrelated content can't match. NOTE: this is an interim guard; the
  // robust fix is to store PRE-disclaimer content and inject only at send
  // (tracked as a follow-up) — a hostile advisor could still hand-craft the
  // marker to suppress, which store-at-send eliminates.
  const DISCLAIMER_MARKER = '<p data-compliance-disclaimer="1"';
  const escapedDisclaimer = escapeHtmlText(disclaimer);
  if (
    typeof html === 'string' &&
    (html.includes(DISCLAIMER_MARKER) || html.includes(escapedDisclaimer))
  ) {
    return html;
  }

  return `${html}<p data-compliance-disclaimer="1" style="font-size:11px;color:#888;margin-top:24px;line-height:1.5">${escapedDisclaimer}</p>`;
}
